// Etapp- och områdesmärkning: samlad plats där risksammanställningar,
// arbetsberedningar och arbetsområdesutredningar knyts till rätt zon (område)
// och etapp, så risker blir korrekta per zon och tidplanen får rätt underlag.
// Systemet ger auto-förslag som användaren bekräftar.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tags, Sparkles, Check, MapPin, AlertTriangle } from "lucide-react";
import {
  suggestArea,
  suggestStage,
  suggestDatesFromStage,
  type SuggestStage,
} from "@/lib/basProcess/stageSuggest";

interface Props {
  projectId: string;
  /** Anropas när något märkts om, så tidplanen kan importeras på nytt. */
  onChanged?: () => void;
}

interface PrepRow {
  id: string;
  name: string;
  stage_id: string | null;
  area_survey_id: string | null;
  area: string;
  start: string;
  end: string;
  data: Record<string, unknown> | null;
}

interface SurveyRow {
  id: string;
  area_name: string;
  stage_id: string | null;
  planned_start: string | null;
  planned_end: string | null;
}

interface RiskRow {
  id: string;
  name: string;
  phase: string;
  stage_id: string | null;
  area_survey_id: string | null;
}

const NONE = "__none__";

export const StageTagging: React.FC<Props> = ({ projectId, onChanged }) => {
  const [stages, setStages] = useState<SuggestStage[]>([]);
  const [preps, setPreps] = useState<PrepRow[]>([]);
  const [surveys, setSurveys] = useState<SurveyRow[]>([]);
  const [risks, setRisks] = useState<RiskRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [stageRes, prepRes, surveyRes, riskRes] = await Promise.all([
      supabase
        .from("project_stages")
        .select("id, name, start_date, end_date")
        .eq("project_id", projectId)
        .order("sort_order", { ascending: true }),
      supabase
        .from("work_preparations")
        .select("id, name, data, stage_id, area_survey_id")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false }),
      supabase
        .from("work_area_surveys")
        .select("id, area_name, stage_id, planned_start, planned_end")
        .eq("project_id", projectId)
        .order("created_at", { ascending: true }),
      supabase
        .from("folder_risk_assessments")
        .select("id, name, phase, stage_id, area_survey_id")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false }),
    ]);

    setStages((stageRes.data || []) as SuggestStage[]);
    setPreps(
      ((prepRes.data || []) as Array<{
        id: string;
        name: string;
        data: unknown;
        stage_id: string | null;
        area_survey_id: string | null;
      }>).map((p) => {
        const data = (p.data as Record<string, unknown> | null) || null;
        const f =
          ((data as { fields?: Record<string, string> } | null)?.fields || {}) as Record<
            string,
            string
          >;
        return {
          id: p.id,
          name: p.name,
          stage_id: p.stage_id,
          area_survey_id: p.area_survey_id,
          area: f.arbetsplats || "",
          start: f.startdatum || "",
          end: f.slutdatum || "",
          data,
        };
      })
    );
    setSurveys((surveyRes.data || []) as SurveyRow[]);
    setRisks((riskRes.data || []) as RiskRow[]);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const areaNames = useMemo(
    () => surveys.map((s) => s.area_name).filter(Boolean),
    [surveys]
  );
  const areaByName = useMemo(() => {
    const m: Record<string, string> = {};
    surveys.forEach((s) => {
      if (s.area_name) m[s.area_name] = s.id;
    });
    return m;
  }, [surveys]);
  const stageName = (id?: string | null) =>
    stages.find((s) => s.id === id)?.name || "etapp";
  const areaName = (id?: string | null) =>
    surveys.find((s) => s.id === id)?.area_name || "område";

  /** Förslag per arbetsberedning: etapp och zon. */
  const prepSuggestions = useMemo(() => {
    const map: Record<
      string,
      { stage?: { value: string; reason: string }; area?: { value: string; reason: string } }
    > = {};
    preps.forEach((p) => {
      const text = [p.name, p.area].filter(Boolean).join(" ");
      const entry: (typeof map)[string] = {};
      if (!p.stage_id) {
        const s = suggestStage(text, stages, { start: p.start, end: p.end });
        if (s) entry.stage = s;
      }
      if (!p.area_survey_id) {
        const a = suggestArea(text, areaNames);
        if (a && areaByName[a.value]) entry.area = { value: areaByName[a.value], reason: a.reason };
      }
      if (entry.stage || entry.area) map[p.id] = entry;
    });
    return map;
  }, [preps, stages, areaNames, areaByName]);

  /** Förslag per risksammanställning: zon och etapp utifrån dokumentets namn. */
  const riskSuggestions = useMemo(() => {
    const map: Record<
      string,
      { stage?: { value: string; reason: string }; area?: { value: string; reason: string } }
    > = {};
    risks.forEach((r) => {
      const entry: (typeof map)[string] = {};
      if (!r.stage_id) {
        const s = suggestStage(r.name, stages);
        if (s) entry.stage = s;
      }
      if (!r.area_survey_id) {
        const a = suggestArea(r.name, areaNames);
        if (a && areaByName[a.value]) entry.area = { value: areaByName[a.value], reason: a.reason };
      }
      if (entry.stage || entry.area) map[r.id] = entry;
    });
    return map;
  }, [risks, stages, areaNames, areaByName]);

  /** Förslag per arbetsområde: etapp och datum från etappen. */
  const surveySuggestions = useMemo(() => {
    const map: Record<
      string,
      { stage?: { value: string; reason: string }; dates?: { start: string; end: string } }
    > = {};
    surveys.forEach((s) => {
      const entry: (typeof map)[string] = {};
      if (!s.stage_id) {
        const sug = suggestStage(s.area_name, stages, {
          start: s.planned_start,
          end: s.planned_end,
        });
        if (sug) entry.stage = sug;
      }
      const stageId = s.stage_id || entry.stage?.value;
      if (stageId && (!s.planned_start || !s.planned_end)) {
        const d = suggestDatesFromStage(stages.find((st) => st.id === stageId));
        if (d && (d.start || d.end)) entry.dates = d;
      }
      if (entry.stage || entry.dates) map[s.id] = entry;
    });
    return map;
  }, [surveys, stages]);

  const suggestionCount =
    Object.keys(prepSuggestions).length +
    Object.keys(surveySuggestions).length +
    Object.keys(riskSuggestions).length;

  /** Täckning per zon: har varje område en risksammanställning och beredningar? */
  const zoneCoverage = useMemo(
    () =>
      surveys.map((s) => ({
        id: s.id,
        name: s.area_name,
        risks: risks.filter((r) => r.area_survey_id === s.id).length,
        preps: preps.filter((p) => p.area_survey_id === s.id).length,
      })),
    [surveys, risks, preps]
  );
  const zonesWithoutRisk = zoneCoverage.filter((z) => z.risks === 0).length;

  const patchPrep = async (id: string, patch: Partial<PrepRow>) => {
    setPreps((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const dbPatch: Record<string, unknown> = {};
    if ("stage_id" in patch) dbPatch.stage_id = patch.stage_id;
    if ("area_survey_id" in patch) dbPatch.area_survey_id = patch.area_survey_id;
    const { error } = await supabase
      .from("work_preparations")
      .update(dbPatch as never)
      .eq("id", id);
    if (error) return toast.error("Kunde inte spara märkningen");
    onChanged?.();
  };

  const patchRisk = async (id: string, patch: Partial<RiskRow>) => {
    setRisks((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const { error } = await supabase
      .from("folder_risk_assessments")
      .update(patch as never)
      .eq("id", id);
    if (error) return toast.error("Kunde inte spara märkningen");
    onChanged?.();
  };

  const patchSurvey = async (id: string, patch: Partial<SurveyRow>) => {
    setSurveys((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const { error } = await supabase.from("work_area_surveys").update(patch).eq("id", id);
    if (error) return toast.error("Kunde inte spara ändringen");
    onChanged?.();
  };

  /** Bekräftar alla förslag på en gång. */
  const applyAll = async () => {
    for (const [id, sug] of Object.entries(prepSuggestions)) {
      const patch: Partial<PrepRow> = {};
      if (sug.stage) patch.stage_id = sug.stage.value;
      if (sug.area) patch.area_survey_id = sug.area.value;
      if (Object.keys(patch).length) await patchPrep(id, patch);
    }
    for (const [id, sug] of Object.entries(riskSuggestions)) {
      const patch: Partial<RiskRow> = {};
      if (sug.stage) patch.stage_id = sug.stage.value;
      if (sug.area) patch.area_survey_id = sug.area.value;
      if (Object.keys(patch).length) await patchRisk(id, patch);
    }
    for (const [id, sug] of Object.entries(surveySuggestions)) {
      const patch: Partial<SurveyRow> = {};
      if (sug.stage) patch.stage_id = sug.stage.value;
      if (sug.dates) {
        const row = surveys.find((s) => s.id === id);
        if (!row?.planned_start && sug.dates.start) patch.planned_start = sug.dates.start;
        if (!row?.planned_end && sug.dates.end) patch.planned_end = sug.dates.end;
      }
      if (Object.keys(patch).length) await patchSurvey(id, patch);
    }
    toast.success("Förslagen är bekräftade");
  };

  const untagged =
    preps.filter((p) => !p.stage_id || !p.area_survey_id).length +
    surveys.filter((s) => !s.stage_id).length +
    risks.filter((r) => !r.area_survey_id).length;

  if (loading) return null;
  if (!preps.length && !surveys.length && !risks.length) return null;

  const areaSelect = (
    value: string | null,
    onChange: (v: string | null) => void
  ) => (
    <Select
      value={value || NONE}
      onValueChange={(v) => onChange(v === NONE ? null : v)}
      disabled={surveys.length === 0}
    >
      <SelectTrigger>
        <SelectValue placeholder="Välj område" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>Inget område</SelectItem>
        {surveys.map((s) => (
          <SelectItem key={s.id} value={s.id}>
            {s.area_name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const stageSelect = (
    value: string | null,
    onChange: (v: string | null) => void
  ) => (
    <Select
      value={value || NONE}
      onValueChange={(v) => onChange(v === NONE ? null : v)}
      disabled={stages.length === 0}
    >
      <SelectTrigger>
        <SelectValue placeholder="Välj etapp" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>Ingen etapp</SelectItem>
        {stages.map((s) => (
          <SelectItem key={s.id} value={s.id}>
            {s.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Tags className="h-4 w-4" /> Etapp- och områdesmärkning
        </CardTitle>
        <Badge variant={untagged ? "destructive" : "secondary"}>
          {untagged ? `${untagged} omärkta` : "Allt märkt"}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Knyt risksammanställningar, arbetsberedningar och arbetsområden till rätt zon och
          etapp. Då blir riskerna korrekta per område och krockar kan hittas i tidplanen.
        </p>

        {stages.length === 0 && (
          <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
            Projektet har inga etapper ännu. Lägg upp etapper i skedesplanen först, så kan du
            märka dokumenten här.
          </p>
        )}

        {/* Täckning per zon */}
        {zoneCoverage.length > 0 && (
          <div className="rounded-md border p-3">
            <h4 className="mb-2 flex items-center gap-2 text-sm font-medium">
              <MapPin className="h-4 w-4" /> Risktäckning per område
            </h4>
            <div className="space-y-1">
              {zoneCoverage.map((z) => (
                <div
                  key={z.id}
                  className="flex flex-wrap items-center justify-between gap-2 text-sm"
                >
                  <span>{z.name}</span>
                  <span className="flex items-center gap-2">
                    <Badge variant={z.risks ? "secondary" : "destructive"}>
                      {z.risks ? `${z.risks} risksammanställningar` : "Saknar risker"}
                    </Badge>
                    <Badge variant="outline">{z.preps} beredningar</Badge>
                  </span>
                </div>
              ))}
            </div>
            {zonesWithoutRisk > 0 && (
              <p className="mt-2 flex items-center gap-2 text-xs text-destructive">
                <AlertTriangle className="h-3 w-3" />
                {zonesWithoutRisk} område(n) saknar kopplad risksammanställning.
              </p>
            )}
          </div>
        )}

        {suggestionCount > 0 && (
          <div className="flex flex-col gap-2 rounded-md border border-primary/30 bg-primary/5 p-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="flex items-center gap-2 text-sm">
              <Sparkles className="h-4 w-4 text-primary" />
              {suggestionCount} auto-förslag utifrån rubriker och namn. Inget sparas förrän du
              bekräftar.
            </p>
            <Button size="sm" onClick={applyAll}>
              <Check className="mr-1 h-4 w-4" /> Bekräfta alla förslag
            </Button>
          </div>
        )}

        {risks.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Risksammanställningar</h4>
            {risks.map((r) => {
              const sug = riskSuggestions[r.id];
              return (
                <div key={r.id} className="grid gap-2 rounded-md border p-3 sm:grid-cols-3">
                  <div>
                    <p className="text-sm font-medium">{r.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {r.phase === "bruksskede" ? "Bruksskede" : "Byggskede"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">Område (zon)</Label>
                    {areaSelect(r.area_survey_id, (v) => patchRisk(r.id, { area_survey_id: v }))}
                    {sug?.area && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="mt-1 h-7 w-full justify-start px-2 text-xs"
                        onClick={() => patchRisk(r.id, { area_survey_id: sug.area!.value })}
                      >
                        <Sparkles className="mr-1 h-3 w-3" /> Förslag:{" "}
                        {areaName(sug.area.value)}
                      </Button>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Etapp</Label>
                    {stageSelect(r.stage_id, (v) => patchRisk(r.id, { stage_id: v }))}
                    {sug?.stage && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="mt-1 h-7 w-full justify-start px-2 text-xs"
                        onClick={() => patchRisk(r.id, { stage_id: sug.stage!.value })}
                      >
                        <Sparkles className="mr-1 h-3 w-3" /> Förslag:{" "}
                        {stageName(sug.stage.value)}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {preps.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Arbetsberedningar</h4>
            {preps.map((p) => {
              const sug = prepSuggestions[p.id];
              return (
                <div key={p.id} className="grid gap-2 rounded-md border p-3 sm:grid-cols-3">
                  <div>
                    <p className="text-sm font-medium">{p.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {[
                        p.area || "Ingen arbetsplats angiven",
                        [p.start, p.end].filter(Boolean).join(" – "),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">Område (zon)</Label>
                    {areaSelect(p.area_survey_id, (v) => patchPrep(p.id, { area_survey_id: v }))}
                    {sug?.area && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="mt-1 h-7 w-full justify-start px-2 text-xs"
                        onClick={() => patchPrep(p.id, { area_survey_id: sug.area!.value })}
                      >
                        <Sparkles className="mr-1 h-3 w-3" /> Förslag:{" "}
                        {areaName(sug.area.value)}
                      </Button>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Etapp</Label>
                    {stageSelect(p.stage_id, (v) => patchPrep(p.id, { stage_id: v }))}
                    {sug?.stage && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="mt-1 h-7 w-full justify-start px-2 text-xs"
                        onClick={() => patchPrep(p.id, { stage_id: sug.stage!.value })}
                        title={sug.stage.reason}
                      >
                        <Sparkles className="mr-1 h-3 w-3" /> Förslag:{" "}
                        {stageName(sug.stage.value)}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {surveys.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Arbetsområden</h4>
            {surveys.map((s) => {
              const sug = surveySuggestions[s.id];
              return (
                <div key={s.id} className="grid gap-2 rounded-md border p-3 sm:grid-cols-4">
                  <div>
                    <p className="text-sm font-medium">{s.area_name}</p>
                    {sug?.stage && (
                      <p className="mt-1 text-xs text-muted-foreground">{sug.stage.reason}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Etapp</Label>
                    {stageSelect(s.stage_id, (v) => patchSurvey(s.id, { stage_id: v }))}
                    {sug?.stage && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="mt-1 h-7 w-full justify-start px-2 text-xs"
                        onClick={() => patchSurvey(s.id, { stage_id: sug.stage!.value })}
                      >
                        <Sparkles className="mr-1 h-3 w-3" /> Förslag:{" "}
                        {stageName(sug.stage.value)}
                      </Button>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Startar</Label>
                    <Input
                      type="date"
                      value={s.planned_start || ""}
                      onChange={(e) =>
                        patchSurvey(s.id, { planned_start: e.target.value || null })
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Klart</Label>
                    <Input
                      type="date"
                      value={s.planned_end || ""}
                      onChange={(e) => patchSurvey(s.id, { planned_end: e.target.value || null })}
                    />
                    {sug?.dates && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="mt-1 h-7 w-full justify-start px-2 text-xs"
                        onClick={() =>
                          patchSurvey(s.id, {
                            planned_start: s.planned_start || sug.dates!.start || null,
                            planned_end: s.planned_end || sug.dates!.end || null,
                          })
                        }
                      >
                        <Sparkles className="mr-1 h-3 w-3" /> Förslag: etappens datum
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StageTagging;
